import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import DefinitionSelect from "@/components/DefinitionSelect";
import SearchPicker from "@/components/SearchPicker";
import NumericInput from "@/components/NumericInput";
import CurrencyChip, { CURRENCY_SYMBOL } from "@/components/CurrencyChip";
import { useCurrencyConversion, handleCurrencyChange } from "@/lib/currencyConvert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Loader2, Plus, Trash2, Save, Scissors, Package as PackageIcon, Wrench,
  Calculator, Sparkles, TrendingUp, DollarSign, PackagePlus, Search,
} from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString("tr-TR");

/**
 * SupplierSearchable — "Yeni Kumaş Ekle (Reçete)" modalındaki tedarikçi dropdown.
 * İsim/kod üzerinde TR-fold arama.
 */
function SupplierSearchable({ suppliers }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return suppliers;
    return suppliers.filter((s) => matchesAll(q, [s.name, s.code]));
  }, [suppliers, q]);
  return (
    <>
      {suppliers.length > 4 && (
        <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 p-1.5">
          <div className="relative">
            <Search className="w-3 h-3 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="supplier-select-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              placeholder="Tedarikçi ara…"
              className="h-7 text-xs pl-7 bg-slate-950 border-slate-700 rounded-sm"
            />
          </div>
        </div>
      )}
      <SelectItem value="none">— Seçilmedi —</SelectItem>
      {filtered.map((s) => (
        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
      ))}
      {suppliers.length > 0 && filtered.length === 0 && (
        <div className="px-2 py-3 text-center text-xs text-slate-500 italic">
          "{q}" ile eşleşme yok.
        </div>
      )}
    </>
  );
}

/**
 * Product Recipe (BOM + Operations + Costing + AI) sheet.
 * Opened from the Products page card via a "Reçete" button.
 */
export default function RecipeSheet({ product, open, onOpenChange, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bom, setBom] = useState(null);
  const [costing, setCosting] = useState(null);
  const [previewQty, setPreviewQty] = useState(100);

  // Reference libraries
  const [fabrics, setFabrics] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [operations, setOperations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [defs, setDefs] = useState({ fabric_type: [], fabric_name: [], composition: [], color: [], accessory_category: [], accessory_name: [] });
  // FAZ 5 M6: TCMB conversion — RecipeSheet açıldığında rates yükle
  const { rates: fxRates, convert: fxConvert } = useCurrencyConversion(open);
  const [quickCreate, setQuickCreate] = useState(null); // "fabric" | "accessory" | null

  // AI analysis
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const loadAll = async () => {
    if (!product) return;
    setLoading(true);
    try {
      const [bRes, fRes, aRes, oRes, cRes, sRes,
             ftRes, fnRes, cmRes, clRes, acRes, anRes] = await Promise.all([
        api.get(`/products/${product.id}/bom`),
        api.get(`/inventory/fabrics`),
        api.get(`/inventory/accessories`),
        api.get(`/operations`),
        api.get(`/products/${product.id}/bom/costing?quantity=${previewQty}`),
        api.get(`/suppliers`).catch(() => ({ data: [] })),
        api.get(`/definitions/fabric_type`).catch(() => ({ data: [] })),
        api.get(`/definitions/fabric_name`).catch(() => ({ data: [] })),
        api.get(`/definitions/composition`).catch(() => ({ data: [] })),
        api.get(`/definitions/color`).catch(() => ({ data: [] })),
        api.get(`/definitions/accessory_category`).catch(() => ({ data: [] })),
        api.get(`/definitions/accessory_name`).catch(() => ({ data: [] })),
      ]);
      setBom(bRes.data);
      setFabrics(fRes.data || []);
      setAccessories(aRes.data || []);
      setOperations(oRes.data || []);
      setCosting(cRes.data);
      setSuppliers(sRes.data || []);
      const flt = (arr) => (arr || []).filter((x) => x.is_active !== false);
      setDefs({
        fabric_type: flt(ftRes.data), fabric_name: flt(fnRes.data),
        composition: flt(cmRes.data), color: flt(clRes.data),
        accessory_category: flt(acRes.data), accessory_name: flt(anRes.data),
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && product) {
      setAiResult(null);
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  // ---- Editing helpers ------------------------------------------------------
  const patchBom = (patch) => setBom((prev) => ({ ...prev, ...patch }));

  const addFabricLine = () => {
    if (fabrics.length === 0) { toast.error("Önce kumaş ekleyin"); return; }
    // Yeni satır BOŞ açılır → arama kutusu hemen görünür (eskiden ilk kumaş otomatik seçiliyordu)
    patchBom({
      fabric_lines: [
        ...(bom.fabric_lines || []),
        { fabric_id: "", fabric_name: "", fabric_code: "", consumption_per_unit: 0.5, unit: "kg", unit_cost: 0, currency: "TRY" },
      ],
    });
  };

  const updateFabricLine = (i, patch) => {
    const arr = [...(bom.fabric_lines || [])];
    arr[i] = { ...arr[i], ...patch };
    if (patch.fabric_id) {
      const f = fabrics.find((x) => x.id === patch.fabric_id);
      if (f) {
        arr[i].fabric_name = f.name;
        arr[i].fabric_code = f.code;
        // FAZ 5 M5: kumaş seçilince currency + unit_cost otomatik gel
        arr[i].currency = (f.currency || arr[i].currency || "TRY").toUpperCase();
        if (!arr[i].unit_cost) arr[i].unit_cost = Number(f.unit_cost || f.avg_purchase_cost || 0);
      }
    }
    patchBom({ fabric_lines: arr });
  };
  const removeFabricLine = (i) => patchBom({ fabric_lines: bom.fabric_lines.filter((_, x) => x !== i) });

  const addAccessoryLine = () => {
    if (accessories.length === 0) { toast.error("Önce aksesuar ekleyin"); return; }
    const a = accessories[0];
    patchBom({
      accessory_lines: [
        ...(bom.accessory_lines || []),
        {
          accessory_id: a.id, accessory_name: a.name, accessory_code: a.code,
          qty_per_unit: 1, unit: a.unit || "adet",
          unit_cost: Number(a.unit_cost || a.avg_purchase_cost || 0),
          currency: (a.currency || "TRY").toUpperCase(),  // FAZ 5 M5
        },
      ],
    });
  };
  const updateAccessoryLine = (i, patch) => {
    const arr = [...(bom.accessory_lines || [])];
    arr[i] = { ...arr[i], ...patch };
    if (patch.accessory_id) {
      const a = accessories.find((x) => x.id === patch.accessory_id);
      if (a) {
        arr[i].accessory_name = a.name;
        arr[i].accessory_code = a.code;
        arr[i].unit = a.unit || "adet";
        arr[i].currency = (a.currency || arr[i].currency || "TRY").toUpperCase();
        if (!arr[i].unit_cost) arr[i].unit_cost = Number(a.unit_cost || a.avg_purchase_cost || 0);
      }
    }
    patchBom({ accessory_lines: arr });
  };
  const removeAccessoryLine = (i) => patchBom({ accessory_lines: bom.accessory_lines.filter((_, x) => x !== i) });

  const addOperationLine = (op = null) => {
    const template = op || operations[0];
    if (!template) { toast.error("Önce operasyon kütüphanesine giriş yapın"); return; }
    patchBom({
      operation_lines: [
        ...(bom.operation_lines || []),
        {
          operation_id: template.id,
          name: template.name,
          department: template.department,
          minutes_per_unit: template.minutes_per_unit,
          cost_per_minute: template.cost_per_minute,
          currency: (template.currency || "TRY").toUpperCase(),
        },
      ],
    });
  };
  const updateOperationLine = (i, patch) => {
    const arr = [...(bom.operation_lines || [])];
    arr[i] = { ...arr[i], ...patch };
    patchBom({ operation_lines: arr });
  };
  const removeOperationLine = (i) => patchBom({ operation_lines: bom.operation_lines.filter((_, x) => x !== i) });

  const save = async () => {
    if ((bom.fabric_lines || []).some((l) => !l.fabric_id)) { toast.error("Kumaş seçilmemiş satır var — kumaş seçin veya satırı silin"); return; }
    setSaving(true);
    try {
      const payload = {
        fabric_lines: (bom.fabric_lines || []).map((l) => ({
          fabric_id: l.fabric_id,
          consumption_per_unit: Number(l.consumption_per_unit || 0),
          unit: l.unit || "kg",
          unit_cost: Number(l.unit_cost || 0),
          currency: (l.currency || "TRY").toUpperCase(),
        })),
        accessory_lines: (bom.accessory_lines || []).map((l) => ({
          accessory_id: l.accessory_id,
          qty_per_unit: Number(l.qty_per_unit || 0),
          unit: l.unit || "adet",
          unit_cost: Number(l.unit_cost || 0),
          currency: (l.currency || "TRY").toUpperCase(),
        })),
        operation_lines: (bom.operation_lines || []).map((l) => ({
          operation_id: l.operation_id || "",
          name: l.name,
          department: l.department,
          minutes_per_unit: Number(l.minutes_per_unit || 0),
          cost_per_minute: Number(l.cost_per_minute || 0),
          currency: (l.currency || "TRY").toUpperCase(),
          notes: l.notes || "",
        })),
        overhead_percent: Number(bom.overhead_percent || 0),
        target_margin_percent: Number(bom.target_margin_percent || 0),
      };
      await api.put(`/products/${product.id}/bom`, payload);
      const { data: c } = await api.get(`/products/${product.id}/bom/costing?quantity=${previewQty}`);
      setCosting(c);
      toast.success("Reçete kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runAI = async () => {
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await api.post(`/products/${product.id}/bom/ai-analyze`);
      setAiResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  const recomputePreview = async () => {
    if (!product) return;
    try {
      const { data } = await api.get(`/products/${product.id}/bom/costing?quantity=${previewQty}`);
      setCosting(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (open && bom) recomputePreview(); /* eslint-disable-next-line */ }, [previewQty]);

  const totals = useMemo(() => {
    const base = costing?.totals || {};
    if (!base.materials_cost && !base.labour_cost) return base;
    // Frontend'te canlı yeniden hesaplama: overhead_percent veya
    // target_margin_percent değişince backend'e tekrar gitmeye gerek yok.
    const materials = Number(base.materials_cost) || 0;
    const labour = Number(base.labour_cost) || 0;
    const overheadPct = Number(bom?.overhead_percent) || 0;
    const marginPct = Number(bom?.target_margin_percent) || 0;
    const overhead = (materials + labour) * overheadPct / 100;
    const unit = materials + labour + overhead;
    const suggested = unit * (1 + marginPct / 100);
    return {
      ...base,
      overhead_percent: overheadPct,
      overhead_cost: overhead,
      unit_cost: unit,
      target_margin_percent: marginPct,
      suggested_price: suggested,
      profit_per_unit: suggested - unit,
    };
  }, [costing, bom?.overhead_percent, bom?.target_margin_percent]);

  const liveForQuantity = useMemo(() => {
    const q = previewQty || 1;
    const uc = Number(totals.unit_cost) || 0;
    const sp = Number(totals.suggested_price) || 0;
    return {
      quantity: q,
      total_cost: uc * q,
      total_suggested_revenue: sp * q,
      total_profit: (sp - uc) * q,
    };
  }, [totals, previewQty]);

  return embedded ? (
    <div data-testid="recipe-embedded" className="text-slate-100 bg-slate-950 border border-slate-800 rounded-sm p-6">
      {loading || !bom ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="text-sm text-slate-300">
          <div className="mb-4 flex items-center gap-3 flex-wrap text-xs text-slate-400">
            {totals.unit_cost ? (
              <>
                <span className="font-mono">Birim Maliyet: <b className="text-blue-400">₺{fmt(totals.unit_cost)}</b></span>
                <span className="text-slate-600">·</span>
                <span className="font-mono">Önerilen Satış: <b className="text-emerald-400">₺{fmt(totals.suggested_price)}</b></span>
                <span className="text-slate-600">·</span>
                <span className="font-mono">Kâr: <b className="text-emerald-400">₺{fmt(totals.profit_per_unit)}</b></span>
              </>
            ) : (
              <span>Henüz reçete yok — aşağıdan ekleyerek başlayın</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Detaylı düzenleme için Ürünler sayfasında ürüne tıklayın ve "Reçete & Maliyet" panelini açın.
          </div>
          <Button onClick={() => window.open(`/urunler`, "_self")} variant="outline" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
            Ürünler Sayfasında Detaylı Düzenle →
          </Button>
        </div>
      )}
    </div>
  ) : (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-slate-950 dark:bg-slate-950 border-l border-slate-800 text-slate-100 w-full sm:max-w-5xl overflow-y-auto p-0"
        data-testid="recipe-sheet"
      >
        <SheetHeader className="px-6 py-5 border-b border-slate-800 bg-slate-900/60 sticky top-0 z-10 backdrop-blur">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün Reçetesi & Maliyet</div>
          <SheetTitle className="text-slate-50 tracking-tight text-xl">{product?.name}</SheetTitle>
          <div className="flex items-center gap-3 flex-wrap text-xs text-slate-400">
            {totals.unit_cost ? (
              <>
                <span className="font-mono">Birim Maliyet: <b className="text-blue-400">₺{fmt(totals.unit_cost)}</b></span>
                <span className="text-slate-600">·</span>
                <span className="font-mono">Önerilen: <b className="text-emerald-400">₺{fmt(totals.suggested_price)}</b></span>
                <span className="text-slate-600">·</span>
                <span className="font-mono">Kâr: <b className="text-emerald-400">₺{fmt(totals.profit_per_unit)}</b></span>
              </>
            ) : (
              <span>Henüz reçete yok — ekleyerek başlayın</span>
            )}
          </div>
        </SheetHeader>

        {loading || !bom ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <div className="p-6">
            <Tabs defaultValue="fabric" className="w-full">
              <TabsList className="bg-slate-900 border border-slate-800 rounded-sm mb-5 flex flex-wrap h-auto">
                <TabsTrigger value="fabric" data-testid="tab-fabric" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm">
                  <Scissors className="w-3.5 h-3.5 mr-1.5" /> Kumaşlar ({bom.fabric_lines?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="accessory" data-testid="tab-accessory" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm">
                  <PackageIcon className="w-3.5 h-3.5 mr-1.5" /> Aksesuarlar ({bom.accessory_lines?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="operation" data-testid="tab-operation" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm">
                  <Wrench className="w-3.5 h-3.5 mr-1.5" /> Operasyonlar ({bom.operation_lines?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="costing" data-testid="tab-costing" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm">
                  <Calculator className="w-3.5 h-3.5 mr-1.5" /> Maliyet Özeti
                </TabsTrigger>
                <TabsTrigger value="ai" data-testid="tab-ai" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Analiz
                </TabsTrigger>
              </TabsList>

              {/* ---- Kumaşlar ------------------------------------------------- */}
              <TabsContent value="fabric" className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-slate-300">Ürün başına kumaş tüketimi ve birim maliyet</div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="new-fabric-quick-btn"
                      onClick={() => setQuickCreate("fabric")}
                      size="sm"
                      variant="outline"
                      className="border-emerald-800 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/60 rounded-sm text-xs"
                      title="Sistemde yoksa yeni kumaş kartı oluştur"
                    >
                      <PackagePlus className="w-3.5 h-3.5 mr-1" /> Yeni Kumaş Ekle
                    </Button>
                    <Button data-testid="add-fabric-line-btn" onClick={addFabricLine} size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Kumaş Ekle
                    </Button>
                  </div>
                </div>
                <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kumaş</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tüketim/Adet</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim Maliyet</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kur</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Satır Maliyeti</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(bom.fabric_lines || []).length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500 text-sm">Henüz kumaş eklenmedi.</TableCell></TableRow>
                      ) : (
                        bom.fabric_lines.map((l, i) => (
                          <TableRow key={i} className="border-slate-800" data-testid={`fabric-row-${i}`}>
                            <TableCell>
                              {/* Mobil/tablet: Select içindeki arama klavyesi resize ile listeyi kapatıyordu → SearchPicker */}
                              <div className="w-52">
                                <SearchPicker
                                  items={fabrics}
                                  value={l.fabric_id}
                                  onChange={(v) => updateFabricLine(i, { fabric_id: v })}
                                  getLabel={(f) => [f.code, f.name].filter(Boolean).join(" · ")}
                                  getSubtitle={(f) => [f.fabric_type, f.color].filter(Boolean).join(" · ")}
                                  filter={(f, q) => matchesAll(q, [f.code, f.name, f.fabric_type, f.color])}
                                  placeholder="Kumaş seç / ara…"
                                  minChars={0}
                                  maxSuggestions={50}
                                  testId={`fabric-select-${i}`}
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <NumericInput step="0.01" min={0}
                                data-testid={`fabric-consumption-${i}`}
                                value={l.consumption_per_unit ?? 0}
                                onChange={(v) => updateFabricLine(i, { consumption_per_unit: v })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-24 font-mono" />
                            </TableCell>
                            <TableCell>
                              <Select value={l.unit || "kg"} onValueChange={(v) => { if (!v) return; updateFabricLine(i, { unit: v }); }}>
                                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                                  <SelectItem value="kg">kg</SelectItem>
                                  <SelectItem value="m">metre</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <NumericInput step="0.01" min={0}
                                data-testid={`fabric-unit-cost-${i}`}
                                value={l.unit_cost ?? 0}
                                onChange={(v) => updateFabricLine(i, { unit_cost: v })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-24 font-mono" />
                            </TableCell>
                            <TableCell>
                              <CurrencyChip
                                testId={`fabric-currency-${i}`}
                                value={l.currency || "TRY"}
                                onChange={handleCurrencyChange({
                                  rates: fxRates, convert: fxConvert,
                                  currentAmount: l.unit_cost, currentCurrency: l.currency || "TRY",
                                  onApply: ({ amount, currency }) => updateFabricLine(i, { unit_cost: amount, currency }),
                                })}
                              />
                            </TableCell>
                            <TableCell className="text-right font-mono text-blue-400 text-xs">
                              {CURRENCY_SYMBOL[(l.currency || "TRY").toUpperCase()] || "₺"}{fmt((Number(l.consumption_per_unit) || 0) * (Number(l.unit_cost) || 0))}
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => removeFabricLine(i)}
                                data-testid={`delete-fabric-line-${i}`}
                                className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm h-8">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </TabsContent>

              {/* ---- Aksesuarlar --------------------------------------------- */}
              <TabsContent value="accessory" className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-slate-300">Ürün başına aksesuar miktarı ve birim maliyet</div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="new-accessory-quick-btn"
                      onClick={() => setQuickCreate("accessory")}
                      size="sm"
                      variant="outline"
                      className="border-emerald-800 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/60 rounded-sm text-xs"
                      title="Sistemde yoksa yeni aksesuar kartı oluştur"
                    >
                      <PackagePlus className="w-3.5 h-3.5 mr-1" /> Yeni Aksesuar Ekle
                    </Button>
                    <Button data-testid="add-accessory-line-btn" onClick={addAccessoryLine} size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Aksesuar Ekle
                    </Button>
                  </div>
                </div>
                <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Aksesuar</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Adet/Ürün</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim Maliyet</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kur</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Satır Maliyeti</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(bom.accessory_lines || []).length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500 text-sm">Henüz aksesuar eklenmedi.</TableCell></TableRow>
                      ) : (
                        bom.accessory_lines.map((l, i) => (
                          <TableRow key={i} className="border-slate-800" data-testid={`accessory-row-${i}`}>
                            <TableCell>
                              <SearchPicker
                                items={accessories}
                                value={l.accessory_id}
                                onChange={(v) => updateAccessoryLine(i, { accessory_id: v })}
                                getLabel={(a) => a.name}
                                getSubtitle={(a) => [a.code, a.category].filter(Boolean).join(" · ")}
                                placeholder="Aksesuar adı veya kodu ile arayın…"
                                emptyText="Aksesuar yok. Stok Yönetimi'nden ekleyin."
                                minChars={1}
                                maxSuggestions={10}
                                testId={`accessory-select-${i}`}
                              />
                            </TableCell>
                            <TableCell>
                              <NumericInput step="0.01" min={0}
                                data-testid={`accessory-qty-${i}`}
                                value={l.qty_per_unit ?? 0}
                                onChange={(v) => updateAccessoryLine(i, { qty_per_unit: v })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-24 font-mono" />
                            </TableCell>
                            <TableCell>
                              <NumericInput step="0.01" min={0}
                                data-testid={`accessory-unit-cost-${i}`}
                                value={l.unit_cost ?? 0}
                                onChange={(v) => updateAccessoryLine(i, { unit_cost: v })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-24 font-mono" />
                            </TableCell>
                            <TableCell>
                              <CurrencyChip
                                testId={`accessory-currency-${i}`}
                                value={l.currency || "TRY"}
                                onChange={handleCurrencyChange({
                                  rates: fxRates, convert: fxConvert,
                                  currentAmount: l.unit_cost, currentCurrency: l.currency || "TRY",
                                  onApply: ({ amount, currency }) => updateAccessoryLine(i, { unit_cost: amount, currency }),
                                })}
                              />
                            </TableCell>
                            <TableCell className="text-right font-mono text-blue-400 text-xs">
                              {CURRENCY_SYMBOL[(l.currency || "TRY").toUpperCase()] || "₺"}{fmt((Number(l.qty_per_unit) || 0) * (Number(l.unit_cost) || 0))}
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => removeAccessoryLine(i)}
                                data-testid={`delete-accessory-line-${i}`}
                                className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm h-8">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </TabsContent>

              {/* ---- Operasyonlar ------------------------------------------- */}
              <TabsContent value="operation" className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-slate-300">Operasyon kütüphanesinden ekle veya inline düzenle</div>
                  <div className="flex gap-2">
                    <div className="w-56">
                      <SearchPicker
                        items={operations}
                        value=""
                        onChange={(id) => {
                          const op = operations.find((o) => o.id === id);
                          if (op) addOperationLine(op);
                        }}
                        getLabel={(o) => o.name}
                        getSubtitle={(o) => `${o.department} (${o.minutes_per_unit} dk)`}
                        placeholder="Kütüphaneden ekle…"
                        minChars={0}
                        maxSuggestions={50}
                        testId="operation-picker"
                      />
                    </div>
                    <Button size="sm" onClick={() => addOperationLine()} className="bg-blue-600 hover:bg-blue-500 rounded-sm text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Boş Ekle
                    </Button>
                  </div>
                </div>
                <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-slate-800">
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Departman</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">dk/Adet</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim/dk</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Para Birimi</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Satır</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(bom.operation_lines || []).length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500 text-sm">Henüz operasyon eklenmedi.</TableCell></TableRow>
                      ) : (
                        bom.operation_lines.map((l, i) => (
                          <TableRow key={i} className="border-slate-800" data-testid={`operation-row-${i}`}>
                            <TableCell>
                              <Input value={l.name || ""} onChange={(e) => updateOperationLine(i, { name: e.target.value })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-40" />
                            </TableCell>
                            <TableCell>
                              <Select value={l.department} onValueChange={(v) => updateOperationLine(i, { department: v })}>
                                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                                  {["kesim", "baski", "nakis", "dikim", "kalite", "paketleme", "sevkiyat"].map((d) => (
                                    <SelectItem key={d} value={d}>{d}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.1" value={l.minutes_per_unit || 0}
                                onChange={(e) => updateOperationLine(i, { minutes_per_unit: e.target.value })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-20 font-mono" />
                            </TableCell>
                            <TableCell>
                              <Input type="number" step="0.1" value={l.cost_per_minute || 0}
                                onChange={(e) => updateOperationLine(i, { cost_per_minute: e.target.value })}
                                className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-20 font-mono" />
                            </TableCell>
                            <TableCell>
                              <CurrencyChip
                                testId={`operation-currency-${i}`}
                                value={l.currency || "TRY"}
                                onChange={handleCurrencyChange({
                                  rates: fxRates, convert: fxConvert,
                                  currentAmount: l.cost_per_minute, currentCurrency: l.currency || "TRY",
                                  onApply: ({ amount, currency }) => updateOperationLine(i, { cost_per_minute: amount, currency }),
                                })}
                              />
                            </TableCell>
                            <TableCell className="text-right font-mono text-blue-400 text-xs">
                              {CURRENCY_SYMBOL[(l.currency || "TRY").toUpperCase()] || "₺"}{fmt((Number(l.minutes_per_unit) || 0) * (Number(l.cost_per_minute) || 0))}
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => removeOperationLine(i)}
                                data-testid={`delete-operation-line-${i}`}
                                className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm h-8">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </TabsContent>

              {/* ---- Maliyet Özeti ------------------------------------------ */}
              <TabsContent value="costing" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Genel Gider (%)</Label>
                    <Input type="number" step="0.5" value={bom.overhead_percent ?? ""}
                      onChange={(e) => patchBom({ overhead_percent: e.target.value })}
                      placeholder="0"
                      data-testid="overhead-percent-input"
                      className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Hedef Kâr Marjı (%)</Label>
                    <Input type="number" step="0.5" value={bom.target_margin_percent ?? ""}
                      onChange={(e) => patchBom({ target_margin_percent: e.target.value })}
                      placeholder="0"
                      data-testid="margin-percent-input"
                      className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Adet Önizleme</Label>
                    <Input type="number" value={previewQty}
                      onChange={(e) => setPreviewQty(Math.max(1, parseInt(e.target.value) || 1))}
                      data-testid="preview-qty-input"
                      className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
                  </div>
                </div>

                {costing && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono -mb-1">
                      Güncel TCMB Kuruna Göre Tahmini Maliyet · TRY karşılıkları
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Card className="bg-gradient-to-br from-blue-950/40 to-slate-900 border-blue-900/50 rounded-sm p-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">Malzeme</div>
                        <div className="text-xl font-bold font-mono text-blue-300">₺{fmt(totals.materials_cost)}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-1">
                          Kumaş ₺{fmt(totals.fabric_cost)} + Aks. ₺{fmt(totals.accessory_cost)}
                        </div>
                      </Card>
                      <Card className="bg-gradient-to-br from-purple-950/40 to-slate-900 border-purple-900/50 rounded-sm p-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">İşçilik</div>
                        <div className="text-xl font-bold font-mono text-purple-300">₺{fmt(totals.labour_cost)}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-1">
                          {fmt(totals.total_minutes)} dk / adet
                        </div>
                      </Card>
                      <Card className="bg-gradient-to-br from-blue-950/40 to-slate-900 border-blue-900/50 rounded-sm p-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">Birim Maliyet</div>
                        <div className="text-2xl font-bold font-mono text-blue-400" data-testid="unit-cost-value">₺{fmt(totals.unit_cost)}</div>
                        <div className="text-[10px] text-slate-500 font-mono mt-1">
                          GG %{fmt(totals.overhead_percent)} dahil
                        </div>
                      </Card>
                      <Card className="bg-gradient-to-br from-emerald-950/40 to-slate-900 border-emerald-900/50 rounded-sm p-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">Önerilen Satış</div>
                        <div className="text-2xl font-bold font-mono text-emerald-400" data-testid="suggested-price-value">₺{fmt(totals.suggested_price)}</div>
                        <div className="text-[10px] text-emerald-500/70 font-mono mt-1">
                          Kâr ₺{fmt(totals.profit_per_unit)} · %{fmt(totals.target_margin_percent)}
                        </div>
                      </Card>
                    </div>

                    <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-blue-500" />
                        <div className="text-sm font-semibold text-slate-100">
                          {fmtInt(previewQty)} adetlik senaryo
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam Maliyet</div>
                          <div className="text-lg font-mono font-bold text-slate-100">₺{fmt(liveForQuantity.total_cost)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam Ciro</div>
                          <div className="text-lg font-mono font-bold text-emerald-400">₺{fmt(liveForQuantity.total_suggested_revenue)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam Kâr</div>
                          <div className="text-lg font-mono font-bold text-emerald-400" data-testid="total-profit-value">₺{fmt(liveForQuantity.total_profit)}</div>
                        </div>
                      </div>
                    </Card>
                  </>
                )}
              </TabsContent>

              {/* ---- AI Analiz --------------------------------------------- */}
              <TabsContent value="ai" className="space-y-3">
                <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-blue-500" />
                        <div className="text-sm font-semibold text-slate-100">AI Maliyet Optimizasyon Analizi</div>
                      </div>
                      <div className="text-xs text-slate-400">
                        Claude Sonnet 4.6, mevcut reçetenizi inceler; malzeme ve işçilik önerileri sunar.
                      </div>
                    </div>
                    <Button onClick={runAI} disabled={aiLoading} data-testid="ai-analyze-btn"
                      className="bg-blue-600 hover:bg-blue-500 rounded-sm">
                      {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Analiz Et</>}
                    </Button>
                  </div>
                </Card>
                {aiResult && (
                  <div className="space-y-3" data-testid="ai-result-card">
                    <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className={
                          aiResult.overall_verdict === "iyi" ? "bg-emerald-600" :
                          aiResult.overall_verdict === "kotu" ? "bg-red-600" : "bg-amber-600"
                        }>{aiResult.overall_verdict?.toUpperCase()}</Badge>
                        <div className="text-slate-300 text-sm">Verimlilik Skoru: <b className="text-blue-400">{aiResult.cost_efficiency_score}/100</b></div>
                        <div className="text-slate-300 text-sm">Tahmini Tasarruf: <b className="text-emerald-400">%{aiResult.estimated_savings_percent}</b></div>
                      </div>
                      <div className="text-sm text-slate-200 leading-relaxed">{aiResult.summary}</div>
                    </Card>
                    {aiResult.material_suggestions?.length > 0 && (
                      <SuggestionCard title="Malzeme Önerileri" items={aiResult.material_suggestions} color="blue" />
                    )}
                    {aiResult.labour_suggestions?.length > 0 && (
                      <SuggestionCard title="İşçilik Önerileri" items={aiResult.labour_suggestions} color="purple" />
                    )}
                    {aiResult.pricing_suggestions?.length > 0 && (
                      <SuggestionCard title="Fiyatlandırma Önerileri" items={aiResult.pricing_suggestions} color="emerald" />
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="sticky bottom-0 -mx-6 mt-6 px-6 py-3 border-t border-slate-800 bg-slate-950/95 backdrop-blur flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}
                className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
                Kapat
              </Button>
              <Button onClick={save} disabled={saving} data-testid="save-recipe-btn"
                className="bg-blue-600 hover:bg-blue-500 rounded-sm">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Kaydet & Hesapla</>}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
      {/* Quick-create material dialog — Sheet DOM içine yerleştirilirse toast/select
         portal problemi olmasın diye Sheet'in altında ama Sheet root'un içinde. */}
      {quickCreate && (
        <QuickMaterialCreate
          type={quickCreate}
          onClose={() => setQuickCreate(null)}
          suppliers={suppliers}
          defs={defs}
          onDefCreated={(kind, item) => setDefs((p) => ({ ...p, [kind]: [...(p[kind] || []), item] }))}
          onCreated={(item) => {
            if (quickCreate === "fabric") {
              setFabrics((prev) => [...prev, item].sort((a, b) => (a.code || "").localeCompare(b.code || "")));
            } else {
              setAccessories((prev) => [...prev, item].sort((a, b) => (a.code || "").localeCompare(b.code || "")));
            }
            setQuickCreate(null);
          }}
        />
      )}
    </Sheet>
  );
}

/* ------------------------------------------------------------------------ */
/* Reçete içinden yeni kumaş/aksesuar oluşturmak için hafif dialog          */
/* ------------------------------------------------------------------------ */
function QuickMaterialCreate({ type, onClose, suppliers, defs, onCreated, onDefCreated }) {
  const isFabric = type === "fabric";
  const empty = isFabric
    ? { code: "", name: "", fabric_type: "", composition: "", color: "", primary_supplier_id: "" }
    : { code: "", name: "", category: "", unit: "adet", primary_supplier_id: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.name?.trim()) missing.push(isFabric ? "Kumaş Adı" : "Aksesuar Adı");
    if (isFabric) {
      if (!form.fabric_type?.trim()) missing.push("Kumaş Türü");
      if (!form.composition?.trim()) missing.push("Kompozisyon");
    } else {
      if (!form.category?.trim()) missing.push("Kategori");
    }
    if (!form.primary_supplier_id?.trim()) missing.push("Tedarikçi");
    if (missing.length) {
      toast.error(`Zorunlu alan(lar): ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const endpoint = isFabric ? "/inventory/fabrics" : "/inventory/accessories";
      const payload = isFabric
        ? { ...form, weight_gsm: 0, stock_kg: 0, stock_metre: 0, min_stock_kg: 0, location: "" }
        : { ...form, stock: 0, min_stock: 0, location: "" };
      const { data } = await api.post(endpoint, payload);
      toast.success(`${data.code} — ${data.name} kataloğa eklendi`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <PackagePlus className="w-4 h-4 text-emerald-500" />
            {isFabric ? "Yeni Kumaş Ekle (Reçete)" : "Yeni Aksesuar Ekle (Reçete)"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kod</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder={`Boş bırakılırsa otomatik (${isFabric ? "KMS" : "AKS"}-###)`}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500 font-mono"
              />
            </div>
            {isFabric ? (
              <DefinitionSelect
                label="Kumaş Türü"
                required
                value={form.fabric_type}
                onChange={(v) => setForm({ ...form, fabric_type: v })}
                items={defs.fabric_type}
                kind="fabric_type"
                testId="rc-fabric-type"
                placeholder="Kumaş türü seçin"
                addLabel="Yeni Tür"
                onCreated={(item) => onDefCreated?.("fabric_type", item)}
              />
            ) : (
              <DefinitionSelect
                label="Kategori"
                required
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v })}
                items={defs.accessory_category}
                kind="accessory_category"
                testId="rc-acc-cat"
                placeholder="Kategori seçin"
                addLabel="Yeni Kategori"
                onCreated={(item) => onDefCreated?.("accessory_category", item)}
              />
            )}
          </div>
          <DefinitionSelect
            label={isFabric ? "Kumaş Adı" : "Aksesuar Adı"}
            required
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            items={isFabric ? defs.fabric_name : defs.accessory_name}
            kind={isFabric ? "fabric_name" : "accessory_name"}
            testId={isFabric ? "rc-fabric-name" : "rc-acc-name"}
            placeholder={isFabric ? "Kumaş adı seçin" : "Aksesuar adı seçin"}
            addLabel={isFabric ? "Yeni Kumaş Adı Ekle" : "Yeni Aksesuar Adı Ekle"}
            onCreated={(item) => onDefCreated?.(isFabric ? "fabric_name" : "accessory_name", item)}
          />
          {isFabric && (
            <div className="grid grid-cols-2 gap-3">
              <DefinitionSelect
                label="Kompozisyon"
                required
                value={form.composition}
                onChange={(v) => setForm({ ...form, composition: v })}
                items={defs.composition}
                kind="composition"
                testId="rc-composition"
                placeholder="Kompozisyon seçin"
                addLabel="Yeni Kompozisyon"
                onCreated={(item) => onDefCreated?.("composition", item)}
              />
              <DefinitionSelect
                label="Renk"
                value={form.color}
                onChange={(v) => setForm({ ...form, color: v })}
                items={defs.color}
                kind="color"
                testId="rc-color"
                placeholder="Renk seçin"
                addLabel="Yeni Renk Ekle"
                onCreated={(item) => onDefCreated?.("color", item)}
              />
            </div>
          )}
          {!isFabric && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Birim</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                  <SelectItem value="adet">adet</SelectItem>
                  <SelectItem value="metre">metre</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="kon">kon</SelectItem>
                  <SelectItem value="paket">paket</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
              Birincil Tedarikçi<span className="text-rose-400 ml-1">*</span>
            </Label>
            <Select
              value={form.primary_supplier_id || "none"}
              onValueChange={(v) => setForm({ ...form, primary_supplier_id: v === "none" ? "" : v })}
            >
              <SelectTrigger className={`bg-slate-950 border-slate-700 rounded-sm ${!form.primary_supplier_id ? "border-rose-700" : ""}`}
                data-testid="supplier-select-trigger">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-60">
                <SupplierSearchable
                  suppliers={suppliers.filter((s) =>
                    isFabric ? (s.category === "kumas" || s.category === "genel")
                             : (s.category === "aksesuar" || s.category === "genel"))}
                />
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
              İptal
            </Button>
            <Button type="submit" disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ekle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionCard({ title, items, color = "blue" }) {
  const map = {
    blue: "border-blue-900/50 bg-blue-950/20 text-blue-300",
    purple: "border-purple-900/50 bg-purple-950/20 text-purple-300",
    emerald: "border-emerald-900/50 bg-emerald-950/20 text-emerald-300",
  };
  return (
    <Card className={`${map[color]} border rounded-sm p-4`}>
      <div className="text-xs uppercase tracking-widest font-mono mb-2 opacity-80">{title}</div>
      <ul className="space-y-1.5 text-sm text-slate-200">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2"><span className="opacity-60">→</span><span>{it}</span></li>
        ))}
      </ul>
    </Card>
  );
}
